// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Link } from "@material-ui/core";
import { mdiCalculator, mdiCancel, mdiDelete, mdiPlus } from "@mdi/js";
import { Button, Modal, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { EVA_RESEARCH_NAME } from "constants/app.constants";
import {
  setIsUpdateGeoCalculationJobModalOpen,
  setSelectedGeoCalculationJob
} from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { uid } from "utils";

import { useLengthConversion } from "hooks/useUnitConversion";

import {
  generateFilterQuery,
  getFilterFromQuery,
  getWellsFromFilterId
} from "api/filter";
import { getUsersInOrg } from "api/users";

import { EntityKind } from "models/entityKind";
import { IFilterState } from "models/filter";

import { useGetCategoryList } from "components/groupBy/hooks/useGetCategoryList";

import { Heading } from "../base";
import { ActionIconButton } from "./ActionIconButton";
import { StatusPill } from "./StatusPill";
import { useCalculateGeoModel } from "./hooks/useCalculateGeoModel";
import { NO_GEO_SOURCE_MESSAGE, useCanCreateGeoJob } from "./hooks/useCanCreateGeoJob";
import { useCancelGeoCalculationJob } from "./hooks/useCancelGeoCalculationJob";
import { useDeleteGeoCalculationJob } from "./hooks/useDeleteGeoCalculationJob";
import useFilteredGeoCalculationsJobs from "./hooks/useFilteredGeoCalculationsJobs";
import { useGetAllAvailableSavedFilters } from "./hooks/useGetAllAvailableSavedFilters";
import { useGetGeoCalculationsJobs } from "./hooks/useGetGeoCalculationsJobs";
import { useGetModelSourceBoundingBox } from "./hooks/useGetModelSourceBoundingBox";
import { useJobStatus } from "./hooks/useJobStatus";
import {
  getPrimarySourceDisplayName,
  useSurveyDataSources
} from "./hooks/useSurveyDataSources";
import { useTableScrollHeight } from "./hooks/useTableScrollHeight";
import { useUpdateGeoCalculation } from "./hooks/useUpdateGeoCalculation";
import { UpdateGeoCalculationJobModal } from "./modals";
import { CreateGeoCalculationJobModal } from "./modals/CreateGeoCalculationJobModal";
import { GeoCalculationsJob, ScheduleFrequency } from "./models/GeoCalculations";
import { GeoModelJobStatus } from "./models/GeoModelJob";
import { calculateInfoIcon, calculateReportNotice } from "./utils";

export function OrganizationGeoCalculationJobs() {
  const dispatch = useDispatch();
  const { getCategoryListWithUnit } = useGetCategoryList();
  const { convert, getDisplayUnit } = useLengthConversion();
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const getGeoCalculationJobs = useGetGeoCalculationsJobs();
  const {
    ref: tableRef,
    height: tableHeight,
    bodyHeight: tableScrollY
  } = useTableScrollHeight();
  const filteredJobs = useFilteredGeoCalculationsJobs(
    getGeoCalculationJobs.data
  ) as GeoCalculationsJob[];
  const deleteGeoCalculationJob = useDeleteGeoCalculationJob();
  const cancelGeoCalculationJob = useCancelGeoCalculationJob();
  const calculateGeoModel = useCalculateGeoModel();
  const getModelSourceBoundingBox = useGetModelSourceBoundingBox();
  const updateGeoCalculation = useUpdateGeoCalculation();
  const { savedFilters } = useGetAllAvailableSavedFilters();
  const surveySources = useSurveyDataSources();

  const [jobStatus, setJobStatus] = useState<{ [key: string]: string }>({});
  const [showModal, setShowModal] = useState(false);
  // EVA-150: `isLoading` is surfaced, not folded into canCreate. While the private-source list is
  // in flight canCreate is false, so treating pending as denied would show an org whose only geo
  // source is a private model an error saying it has none. Disable the button instead.
  const { canCreate: canCreateGeoJob, isLoading: isGeoSourcesLoading } =
    useCanCreateGeoJob();
  const orgId = useSelector((state: RootState) => state.auth.user.organization.id);
  useEffect(() => {
    const fetchUsers = async () => {
      const users = await getUsersInOrg(orgId);
      setUsers(users);
    };
    fetchUsers();
  }, [orgId]);

  const formattedJobs: GeoCalculationsJob[] = useMemo(
    () =>
      filteredJobs?.map((job) => {
        const date =
          job?.lastSuccessfulRunDate &&
          typeof job.lastSuccessfulRunDate === "object" &&
          "$date" in job.lastSuccessfulRunDate
            ? job.lastSuccessfulRunDate.$date?.$numberLong
            : null;
        const newDate = date ? new Date(parseInt(date)).toLocaleString() : null;

        const nextRunDate =
          job?.nextScheduledRun &&
          typeof job.nextScheduledRun === "object" &&
          "$date" in job.nextScheduledRun
            ? job.nextScheduledRun.$date?.$numberLong
            : null;
        const formattedNextRunDate = nextRunDate
          ? new Date(parseInt(nextRunDate)).toLocaleDateString()
          : null;

        let matchingUser = users.find((user) => user.id === job.createdBy);
        // if matching user is undefined, try matching by email
        if (!matchingUser) {
          matchingUser = users.find((user) => user.email === job.email);
        }
        const newJob = {
          ...job,
          dimensions: `(${convert(job.leftWidth, 2)}x${convert(
            job.rightWidth,
            2
          )}x${convert(job.upHeight, 2)}x${convert(job.downHeight, 2)})`,
          calculations: `${
            (job.includeSampled3D ?? true) && job.includeXda
              ? "Sampled 3D, XDA"
              : job.includeSampled3D ?? true
              ? "Sampled 3D"
              : job.includeXda
              ? "XDA"
              : "None"
          }`,
          createdBy:
            matchingUser && matchingUser.firstName && matchingUser.lastName
              ? matchingUser.firstName + " " + matchingUser.lastName
              : "Unknown",
          modelSource: job.modelSource || "",
          formattedLastSuccessfulRunDate: newDate,
          formattedNextRunDate,
          scheduleFrequencyLabel:
            job.scheduleFrequency === ScheduleFrequency.Weekly
              ? "Weekly"
              : job.scheduleFrequency === ScheduleFrequency.Monthly
              ? "Monthly"
              : null
        };

        return newJob;
      }),
    [filteredJobs, users, convert]
  );

  // Calculate visible jobs for the current page
  const visibleJobs = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return formattedJobs?.slice(startIndex, endIndex) || [];
  }, [formattedJobs, currentPage, pageSize]);

  // Only poll status for visible jobs on current page
  useJobStatus(visibleJobs, jobStatus, setJobStatus);

  // Same seeding as the pill: prefer the live poll status, fall back to the
  // list's server status so the action buttons match the pill before the first
  // poll resolves.
  function currentStatus(record: GeoCalculationsJob) {
    return jobStatus[record.jobId] ?? record.status;
  }

  function isActive(record: GeoCalculationsJob) {
    const status = currentStatus(record);
    return (
      status &&
      status != GeoModelJobStatus.PartiallyCompleted &&
      status != GeoModelJobStatus.Completed &&
      status != GeoModelJobStatus.NotStarted &&
      status != GeoModelJobStatus.Error &&
      status != GeoModelJobStatus.Cancelled &&
      // We're already cancelling the current run of the job
      // It's safe to allow user to schedule a new calculation
      status != GeoModelJobStatus.Cancelling
    );
  }

  // Because the table uses `scroll={{ x: "max-content" }}` (table-layout: fixed),
  // antd sizes each unset column to its BODY content — so a column whose rows are
  // all short (e.g. "No", a count, or blank) collapses and squeezes its header.
  // Pin a width on every column whose content can be narrower than its header so
  // the header always stays readable.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnsType<any> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      // Sticky to the left edge so the job name stays visible while scrolling
      // horizontally. A fixed column needs an explicit width to align correctly.
      fixed: "left",
      width: 220
    },
    {
      title: `Dimensions (${getDisplayUnit()})`,
      dataIndex: "dimensions",
      key: "dimensions",
      width: 150
    },
    {
      title: "Filter",
      key: "wellSource",
      width: 140,
      render: (text, record) => {
        const openUpdateModal = () => {
          dispatch(setIsUpdateGeoCalculationJobModalOpen(true));
          dispatch(setSelectedGeoCalculationJob(record));
        };
        if (record.savedFilterId) {
          const filterName = record.savedFilterName ?? record.savedFilterId;
          return (
            <Link onClick={openUpdateModal} underline="always">
              {filterName}
            </Link>
          );
        }
        const wellListCount = record.wellList?.length ?? 0;
        return (
          <Link onClick={openUpdateModal} underline="always">
            {wellListCount}
          </Link>
        );
      }
    },
    {
      title: "Survey Source",
      dataIndex: "primarySource",
      key: "primarySource",
      width: 150,
      render: (value: string) =>
        getPrimarySourceDisplayName(value || undefined, surveySources)
    },
    {
      title: "Geo Source",
      dataIndex: "modelSource",
      key: "modelSource",
      width: 150,
      render: (value: string) => (value === "" ? EVA_RESEARCH_NAME : value)
    },
    {
      title: "Zones",
      dataIndex: "zones",
      key: "zones",
      // Cap the width so a long zone list wraps within the column instead of
      // stretching the table wide now that the table scrolls horizontally.
      width: 220,
      render: (text, record) => {
        return record.zones?.join(", ");
      }
    },
    {
      title: "Calculations",
      dataIndex: "calculations",
      key: "calculations",
      width: 160
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 150
    },
    {
      title: "Last Successful Run",
      dataIndex: "formattedLastSuccessfulRunDate",
      key: "formattedLastSuccessfulRunDate",
      width: 190
    },
    {
      title: "Scheduled",
      key: "scheduled",
      // Pin a width so the column doesn't collapse to its shortest content (e.g.
      // all rows "No"), which would squeeze the "Scheduled" header. Wide enough
      // to hold a scheduled value like "Monthly - Next: 12/31/2026".
      width: 200,
      render: (text, record) => {
        if (!record.isScheduled) {
          return "No";
        }
        const frequency = record.scheduleFrequencyLabel || "-";
        const nextRun = record.formattedNextRunDate || "-";
        return `${frequency} - Next: ${nextRun}`;
      }
    },
    {
      title: "Status",
      key: "status",
      fixed: "right",
      width: 130,
      render: (text, record) => (
        <StatusPill
          // Seed from the list's server status so the pill shows the real status
          // immediately; the live poll (jobStatus) takes over once it resolves.
          // Nullish-coalescing preserves a legit "" status and only falls back when
          // the poll hasn't populated this job yet — avoids the "Checking…" flash.
          status={jobStatus[record.jobId] ?? record.status}
          errorMessage={record.errorMessage}
          invalidItems={record.invalidItems}
        />
      )
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 150,
      render: (text, record) => {
        // A running/queued job is currently being calculated; a cancelling job has a
        // run in flight that hasn't stopped yet. Block recalculate in both cases so
        // we don't stack up concurrent runs against the same job.
        const jobInProgress =
          isActive(record) || currentStatus(record) === GeoModelJobStatus.Cancelling;
        return (
          <ButtonWrapper>
            <Tooltip title="Calculate">
              {/* antd Tooltip never fires over a disabled native button (no mouse
                  events reach it) — the inline-flex span keeps it hoverable. */}
              <span style={{ display: "inline-flex" }}>
                <ActionIconButton
                  data-testid="calculate-job-button"
                  disabled={jobInProgress || calculateGeoModel.isLoading}
                  onClick={async () => {
                    await handleCalculate(record);
                  }}>
                  <Icon path={mdiCalculator} size={1.4} />
                </ActionIconButton>
              </span>
            </Tooltip>
            {isActive(record) && (
              // Cancel only applies to a run that's currently in progress.
              <Tooltip title="Cancel" overlayInnerStyle={{ padding: "10px" }}>
                <ActionIconButton
                  data-testid="cancel-job-button"
                  $variant="neutral"
                  onClick={() => handleCancel(record.jobId)}>
                  <Icon path={mdiCancel} size={1.4} />
                </ActionIconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete" overlayInnerStyle={{ padding: "10px" }}>
              <ActionIconButton
                data-testid="delete-job-button"
                $variant="danger"
                onClick={() => handleDelete(record.jobId)}>
                <Icon path={mdiDelete} size={1.4} />
              </ActionIconButton>
            </Tooltip>
          </ButtonWrapper>
        );
      }
    }
  ];

  async function calculate(id: string) {
    try {
      await calculateGeoModel.mutateAsync(id);
      // Reflect the newly-queued run immediately and resume status polling for
      // this job — it may have been in a terminal state, which stops the poll.
      setJobStatus((prev) => ({ ...prev, [id]: "Queued" }));
      getCategoryListWithUnit(undefined);
      // Only surface success once the mutation actually succeeded — moved here
      // (rather than in each caller) so a failed mutate can never show this.
      toast.info(
        "Job has been queued. A report will be emailed to you and the job owner when it finishes."
      );
    } catch {
      toast.error("An error occurred while calculating the calculation job.");
    }
  }

  async function handleCalculate(record: GeoCalculationsJob) {
    if (record.savedFilterId) {
      let filterHasChanged = false;
      let wellListHasChanged = false;
      let currentSavedFilter = null;
      let currentWellList: string[] = [];

      try {
        // Check if the saved filter has changed
        currentSavedFilter = savedFilters?.find((f) => f.id === record.savedFilterId);
        if (currentSavedFilter && record.savedFilterJson) {
          filterHasChanged = currentSavedFilter.filterJSON !== record.savedFilterJson;
        }

        // Parse the saved filter to get filter state
        let filterState: IFilterState;
        try {
          if (!record.savedFilterJson) {
            toast.error("Saved filter configuration not found.");
            return;
          }
          filterState = JSON.parse(record.savedFilterJson);
        } catch (e) {
          toast.error("Invalid saved filter configuration.");
          return;
        }

        let polygonFilter;
        // Get model source bounding box if no polygon filter exists in the saved filter
        if (!filterState.polygonFilter) {
          try {
            const bbox = await getModelSourceBoundingBox.mutateAsync({
              modelSource: record.modelSource == "McDaniel" ? "" : record.modelSource,
              ...(record.zones?.length > 0 && { zones: record.zones })
            });

            // Convert the bounding box to a polygon filter
            polygonFilter = {
              id: uid(),
              type: "Polygon",
              coordinates: [
                [
                  [bbox.minLng, bbox.minLat],
                  [bbox.maxLng, bbox.minLat],
                  [bbox.maxLng, bbox.maxLat],
                  [bbox.minLng, bbox.maxLat],
                  [bbox.minLng, bbox.minLat]
                ]
              ],
              source: "model-source-bounds",
              operator: "="
            };
          } catch (e) {
            // If bounding box fails, fall back to saved filter's polygon filter
            polygonFilter = filterState.polygonFilter || null;
          }
        } else {
          // Use the saved filter's polygon filter
          polygonFilter = filterState.polygonFilter || null;
        }

        // Generate the filter query using the saved filter state and bounding box
        const query = generateFilterQuery(
          filterState.wellListFilter || [],
          polygonFilter,
          filterState.excludePolygonsFilter || [],
          filterState.propertiesFilter || [],
          null, // previousFilterId
          null, // screenshotPolygon
          EntityKind.Well
        );

        // Get the real filter ID
        const filterResult = await getFilterFromQuery(query);
        if (!filterResult.ok) {
          toast.error("Failed to generate filter. Please try again.");
          return;
        }

        const realFilterId = filterResult.value;

        // Now get the wells from the real filter ID to detect a changed well list
        const wellsResponse = await getWellsFromFilterId(realFilterId);
        if (wellsResponse.data?.uwiList) {
          currentWellList = wellsResponse.data.uwiList.map((uwi) => uwi.uniqueId);
          const recordWellList = record.wellList || [];

          // Compare well lists (order-independent)
          const sortedCurrent = [...currentWellList].sort();
          const sortedRecord = [...recordWellList].sort();

          wellListHasChanged =
            sortedCurrent.length !== sortedRecord.length ||
            sortedCurrent.some((uwi, idx) => uwi !== sortedRecord[idx]);
        }
      } catch (e) {
        toast.error("Failed to load filter details. Please try again.");
        return;
      }

      Modal.confirm({
        title: "Calculate job?",
        icon: calculateInfoIcon,
        content: calculateReportNotice,
        okText: "Calculate",
        onOk: async () => {
          // Update the job if the filter or well list has changed
          if ((filterHasChanged && currentSavedFilter) || wellListHasChanged) {
            try {
              await updateGeoCalculation.mutateAsync({
                ...record,
                ...(filterHasChanged &&
                  currentSavedFilter && {
                    savedFilterJson: currentSavedFilter.filterJSON,
                    savedFilterName: currentSavedFilter.name
                  }),
                ...(wellListHasChanged && {
                  wellList: currentWellList
                })
              });
            } catch (e) {
              toast.error("Failed to update job configuration.");
              return;
            }
          }

          await calculate(record.jobId);
        }
      });
    } else {
      Modal.confirm({
        title: "Calculate job?",
        icon: calculateInfoIcon,
        content: calculateReportNotice,
        okText: "Calculate",
        onOk: async () => {
          await calculate(record.jobId);
        }
      });
    }
  }

  async function handleCancel(id) {
    Modal.confirm({
      title: "Confirm Cancel",
      content: "Cancel the calculation job?",
      onOk: async () => {
        const updatedJobs = await getGeoCalculationJobs.refetch();
        const jobToCancel = updatedJobs.data?.find((job) => job.jobId === id);
        // No run to cancel (job vanished from the list, or has no active run):
        // bail without a misleading "cancel failed" toast or a bad request.
        if (!jobToCancel?.runId) {
          return;
        }
        try {
          await cancelGeoCalculationJob.mutateAsync({
            jobId: id,
            runId: jobToCancel.runId
          });
          // Reflect the cancel immediately. The pill reads the jobStatus poll
          // map (not the jobs list), so without this it keeps showing the last
          // polled progress until the worker actually stops. "Cancelling" is an
          // in-progress status, so polling continues and settles it to terminal.
          setJobStatus((prev) => ({ ...prev, [id]: GeoModelJobStatus.Cancelling }));
        } catch {
          toast.error("An error occurred while cancelling the calculation job.");
        }
      }
    });
  }

  async function handleDelete(id) {
    Modal.confirm({
      title: "Confirm Delete",
      content:
        "Are you sure you want to delete this calculation job and all calculated data?",
      onOk: async () => {
        await deleteGeoCalculationJob
          .mutateAsync(id)
          .catch(() => {
            toast.error("An error occurred while deleting the calculation job.");
          })
          .then(() => {
            getCategoryListWithUnit(undefined);
          });
      }
    });
  }

  if (getGeoCalculationJobs.isError) {
    return <ErrorMessage>Error occurred while fetching tokens</ErrorMessage>;
  }

  return (
    <PageContainer>
      <Heading element="h4">Geo Model Calculations </Heading>
      <ButtonWrapper>
        <Button
          icon={<Icon path={mdiPlus} size={1} />}
          type="primary"
          loading={isGeoSourcesLoading}
          disabled={isGeoSourcesLoading}
          onClick={() => {
            // EVA-150: with no 3D Geo Model subscription and no private models there is no geo
            // source to calculate against, so tell the user instead of opening a modal whose only
            // possible outcome is an empty job.
            if (!canCreateGeoJob) {
              toast.error(NO_GEO_SOURCE_MESSAGE);
              return;
            }
            setShowModal(true);
          }}>
          Add New Calculation Job
        </Button>
      </ButtonWrapper>
      {/* Fixed, clipped height so the PAGE never scrolls — a tall/expanded row
          scrolls inside the table body instead of growing the container. */}
      <TableWrapper ref={tableRef} tableHeight={tableHeight}>
        <Table
          dataSource={formattedJobs}
          columns={columns}
          rowKey="jobId"
          scroll={{ x: "max-content", y: tableScrollY }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: formattedJobs?.length || 0,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} jobs`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }
          }}
        />
      </TableWrapper>

      <UpdateGeoCalculationJobModal />
      <CreateGeoCalculationJobModal
        requestShowModal={showModal}
        jobs={filteredJobs}
        onClose={() => setShowModal(false)}
        savedFilters={savedFilters}
      />
    </PageContainer>
  );
}

// Full-width, shrinkable wrapper so the table stays within the viewport and
// scrolls horizontally on resize instead of overflowing the layout. min-width:0
// lets it shrink inside a flex/grid parent (which otherwise sizes to content).
const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 20px;
  margin: 20px;
  border: 1px solid red;
  padding: 10px;
  background-color: #ffe6e6;
  border-radius: 5px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  .ant-btn {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const TableWrapper = styled.div`
  height: ${({ tableHeight }: { tableHeight: number }) => tableHeight}px;
  overflow: hidden;

  .ant-table-ping-left .ant-table-cell-fix-left-first:after,
  .ant-table-ping-left .ant-table-cell-fix-left-last:after,
  .ant-table-ping-left:not(.ant-table-has-fix-left) > .ant-table-container:before {
    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.05);
  }
  .ant-table-ping-right .ant-table-cell-fix-right-first:after,
  .ant-table-ping-right .ant-table-cell-fix-right-last:after,
  .ant-table-ping-right:not(.ant-table-has-fix-right) > .ant-table-container:before {
    box-shadow: inset -10px 0 8px -8px rgba(0, 0, 0, 0.05);
  }
`;
